import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Clock, Loader2, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
            <div className="w-10 h-10 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center">
              <Clock className="w-6 h-6 text-black" />
            </div>
            <span className="font-semibold text-xl text-[#090000]">New Community Wealth Incubator</span>
          </Link>

          <Card className="">
            <CardContent className="py-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-[#22c55e] mb-4" />
              <h2 className="text-xl font-semibold text-[#090000] mb-2">Check Your Email</h2>
              <p className="text-[#090000/60] mb-4">
                If an account exists with <strong>{email}</strong>, you will receive a password reset link shortly.
              </p>
              <p className="text-sm text-[#090000/60] mb-6">
                Don't see it? Check your spam folder.
              </p>
              <Link to="/login">
                <Button variant="outline" className="">
                  Back to Login
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center" data-testid="forgot-logo">
          <div className="w-10 h-10 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center">
            <Clock className="w-6 h-6 text-black" />
          </div>
          <span className="font-semibold text-xl text-[#090000]">New Community Wealth Incubator</span>
        </Link>

        <Card className="">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-semibold text-[#090000]">Forgot Password?</CardTitle>
            <CardDescription className="text-[#090000/60]">
              Enter your email and we'll send you a reset link
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-lg flex items-start gap-2" data-testid="forgot-error">
                <AlertCircle className="w-5 h-5 text-[#ef4444] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#ef4444]">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#090000]">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#090000/60]" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 focus:ring-[#d1c7e9]"
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full"
                disabled={loading}
                data-testid="forgot-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Reset Link'
                )}
              </Button>
            </form>

            <p className="text-center text-sm text-[#090000/60] mt-6">
              Remember your password?{' '}
              <Link to="/login" className="text-[#ffd703] hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
